import { useState, useEffect, useCallback, useRef } from 'react';
import { Segment } from '~/types';
import { getSegments } from '~/lib/api';

const POLL_INTERVAL_MS = 2000; // 2 seconds

export function useTranscription(sessionId: string | null) {
  const [segments, setSegments] = useState<Map<number, Segment>>(new Map());
  const [latestIndex, setLatestIndex] = useState(-1);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const lastFetchedIndexRef = useRef(-1);

  /**
   * Poll for new transcription results
   */
  const poll = useCallback(async () => {
    if (!sessionId) return;

    try {
      const result = await getSegments(sessionId, lastFetchedIndexRef.current + 1);

      // Update segments map
      setSegments(prev => {
        const newMap = new Map(prev);
        result.segments.forEach(seg => {
          newMap.set(seg.segment_index, seg);
        });
        return newMap;
      });

      // Update latest index
      if (result.segments.length > 0) {
        const maxIndex = Math.max(...result.segments.map(s => s.segment_index));
        lastFetchedIndexRef.current = Math.max(lastFetchedIndexRef.current, maxIndex);
      }

      setLatestIndex(result.latest_index);
    } catch (error) {
      console.error('Failed to poll transcription:', error);
    }
  }, [sessionId]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    if (intervalRef.current !== null || !sessionId) return;

    setIsPolling(true);

    // Poll immediately
    poll();

    // Then poll periodically
    intervalRef.current = window.setInterval(() => {
      poll();
    }, POLL_INTERVAL_MS);
  }, [sessionId, poll]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setSegments(new Map());
    setLatestIndex(-1);
    lastFetchedIndexRef.current = -1;
    stopPolling();
  }, [stopPolling]);

  // Auto-start polling when sessionId changes
  useEffect(() => {
    if (sessionId) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [sessionId, startPolling, stopPolling]);

  /**
   * Get ordered segments array
   */
  const getOrderedSegments = useCallback((): (Segment | null)[] => {
    const result: (Segment | null)[] = [];

    for (let i = 0; i <= latestIndex; i++) {
      result.push(segments.get(i) || null);
    }

    return result;
  }, [segments, latestIndex]);

  return {
    segments,
    latestIndex,
    isPolling,
    getOrderedSegments,
    reset,
  };
}
